import { useState, useEffect } from "react";
import { useLanguage } from "@/contexts/LanguageContext";
import Sidebar from "@/components/Sidebar";
import axios from "axios";
import { authAxios as ax } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Server, Database, Wifi, RefreshCw, CheckCircle, AlertTriangle, XCircle } from "lucide-react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const statusIcon = (s) => {
  if (s === "running" || s === "connected" || s === "configured" || s === "active" || s === "healthy") return <CheckCircle size={16} className="text-[#00C853]" />;
  if (s === "simulated") return <AlertTriangle size={16} className="text-[#FFB800]" />;
  return <XCircle size={16} className="text-[#FF2A2A]" />;
};

const statusColor = (s) => {
  if (s === "running" || s === "connected" || s === "configured" || s === "active" || s === "healthy") return "bg-[#00C853]/10 text-[#00C853]";
  if (s === "simulated") return "bg-[#FFB800]/10 text-[#FFB800]";
  return "bg-[#FF2A2A]/10 text-[#FF2A2A]";
};

export default function SystemAudit() {
  const { t } = useLanguage();
  const [system, setSystem] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const res = await ax.get(`${API}/audit/system`);
      setSystem(res.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to load");
    }
    setLoading(false);
  };

  useEffect(() => { load(); }, []);

  return (
    <div className="flex h-screen bg-[#F8F9FA]">
      <Sidebar />
      <main className="flex-1 overflow-auto">
        <div className="p-8">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-2xl sm:text-3xl font-bold text-[#0A0A0A] tracking-tight" data-testid="audit-title">
              {t("audit.title")}
            </h2>
            <Button
              variant="outline"
              className="rounded-none h-9 text-xs font-bold tracking-[0.1em] uppercase"
              onClick={load}
              disabled={loading}
              data-testid="audit-refresh-btn"
            >
              <RefreshCw size={14} className={`mr-1 ${loading ? "animate-spin" : ""}`} /> {t("audit.refresh")}
            </Button>
          </div>

          {system ? (
            <div className="grid lg:grid-cols-3 gap-6">
              {/* Server Status */}
              <div className="border border-[#E5E7EB] bg-white p-6" data-testid="audit-server">
                <div className="flex items-center gap-3 mb-5">
                  <div className="w-10 h-10 flex items-center justify-center bg-[#002FA7]">
                    <Server size={18} className="text-white" />
                  </div>
                  <div>
                    <h3 className="text-sm font-bold tracking-[0.1em] uppercase text-[#0A0A0A]">{t("audit.server_status")}</h3>
                  </div>
                </div>
                <div className="space-y-3">
                  <div className="flex justify-between items-center py-2 border-b border-[#E5E7EB]">
                    <span className="text-sm text-[#4B5563]">{t("common.status")}</span>
                    <div className="flex items-center gap-2">
                      {statusIcon(system.server.status)}
                      <Badge className={`rounded-none text-[10px] font-bold uppercase ${statusColor(system.server.status)}`}>{t("audit.running")}</Badge>
                    </div>
                  </div>
                  <div className="flex justify-between items-center py-2 border-b border-[#E5E7EB]">
                    <span className="text-sm text-[#4B5563]">Version</span>
                    <span className="text-sm font-mono font-bold text-[#0A0A0A]">{system.server.version}</span>
                  </div>
                  <div className="flex justify-between items-center py-2">
                    <span className="text-sm text-[#4B5563]">Timestamp</span>
                    <span className="text-xs font-mono text-[#9CA3AF]">{new Date(system.server.timestamp).toLocaleTimeString()}</span>
                  </div>
                </div>
              </div>

              {/* Database */}
              <div className="border border-[#E5E7EB] bg-white p-6" data-testid="audit-database">
                <div className="flex items-center gap-3 mb-5">
                  <div className="w-10 h-10 flex items-center justify-center bg-[#0A0A0A]">
                    <Database size={18} className="text-white" />
                  </div>
                  <div>
                    <h3 className="text-sm font-bold tracking-[0.1em] uppercase text-[#0A0A0A]">{t("audit.db_latency")}</h3>
                  </div>
                </div>
                <div className="space-y-3">
                  <div className="flex justify-between items-center py-2 border-b border-[#E5E7EB]">
                    <span className="text-sm text-[#4B5563]">{t("common.status")}</span>
                    <div className="flex items-center gap-2">
                      {statusIcon(system.database.status)}
                      <Badge className={`rounded-none text-[10px] font-bold uppercase ${statusColor(system.database.status)}`}>{t("audit.connected")}</Badge>
                    </div>
                  </div>
                  <div className="flex justify-between items-center py-2 border-b border-[#E5E7EB]">
                    <span className="text-sm text-[#4B5563]">Latency</span>
                    <span className="text-lg font-black text-[#0A0A0A] font-mono">{system.database.latency_ms}ms</span>
                  </div>
                  <div className="py-2">
                    <p className="text-[10px] tracking-[0.2em] uppercase font-bold text-[#9CA3AF] mb-2">{t("audit.collections")}</p>
                    <div className="grid grid-cols-2 gap-2">
                      {Object.entries(system.database.collections || {}).map(([key, val]) => (
                        <div key={key} className="flex justify-between items-center bg-[#F8F9FA] px-3 py-1.5 border border-[#E5E7EB]">
                          <span className="text-xs text-[#4B5563] capitalize">{key}</span>
                          <span className="text-xs font-bold text-[#0A0A0A] font-mono">{val}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>

              {/* API Integrations */}
              <div className="border border-[#E5E7EB] bg-white p-6" data-testid="audit-integrations">
                <div className="flex items-center gap-3 mb-5">
                  <div className="w-10 h-10 flex items-center justify-center bg-[#00C853]">
                    <Wifi size={18} className="text-white" />
                  </div>
                  <div>
                    <h3 className="text-sm font-bold tracking-[0.1em] uppercase text-[#0A0A0A]">{t("audit.api_status")}</h3>
                  </div>
                </div>
                <div className="space-y-3">
                  {Object.entries(system.integrations || {}).map(([key, info]) => (
                    <div key={key} className="flex justify-between items-center py-2 border-b border-[#E5E7EB] last:border-0">
                      <div>
                        <span className="text-sm text-[#0A0A0A] font-medium capitalize">{key.replace(/_/g, " ")}</span>
                        {info.provider && <p className="text-[10px] text-[#9CA3AF]">{info.provider}</p>}
                        {info.url && <p className="text-[10px] text-[#9CA3AF] font-mono">{info.url}</p>}
                      </div>
                      <div className="flex items-center gap-2">
                        {statusIcon(info.status)}
                        <Badge className={`rounded-none text-[10px] font-bold uppercase ${statusColor(info.status)}`}>
                          {info.status === "configured" ? t("audit.configured") : info.status === "simulated" ? t("audit.simulated") : info.status === "not_configured" ? t("audit.not_configured") : info.status}
                        </Badge>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          ) : loading ? (
            <div className="flex items-center justify-center py-20">
              <div className="w-8 h-8 border-2 border-[#002FA7] border-t-transparent rounded-full animate-spin" />
            </div>
          ) : (
            <p className="text-center text-[#9CA3AF] py-20">{t("common.no_data")}</p>
          )}
        </div>
      </main>
    </div>
  );
}
